/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

// ------------------------------------------------------------------------------------------------------------------ //

import * as common from "@esri/solution-common";
import { ITemplateInfoGraph } from "./solutionGraph";

/**
 * Prepares the information for displaying a solution.
 * @param solutionId Id of the solution
 * @param userSession Authenication to fetch solution and, for deployed solutions, each of its items
 * @returns Promise resolving to a collection ready for display
 */
export async function prepareDataForSolutionDisplay(
  solutionId: string,
  userSession: common.UserSession
): Promise<ITemplateInfoGraph[]> {
  // Fetch the solution
  const solutionData = await common.getItemDataAsJson(solutionId, userSession);

  // If the solution was not found, quit
  if (!solutionData) {
    throw new Error(`Solution ${solutionId} was not found`);
  }

  // Get the root URL for opening an item's details
  const portalRoot = userSession?.portal.replace("/sharing/rest", "") ?? "https://www.arcgis.com";

  // Assemble the info for the display
  let collection: ITemplateInfoGraph[] = [];

  // If this is a deployed solution, we'll have to supplement the data with each contained item's title and snippet
  if (!solutionData.templates[0].item) {
    const itemFetchPromises = solutionData.templates.map((template: any) => {
      if (template.type === "Group") {
        return common.getGroupBase(template.itemId as string, userSession);
      } else {
        return common.getItemBase(template.itemId as string, userSession);
      }
    });
    const itemFetches = await Promise.allSettled(itemFetchPromises);
    const items = itemFetches
      .filter((item: any) => item.status === "fulfilled")
      .map((item: any) => item.value);

    collection = items.map((item: any, index: number) => {
      const page = typeof item.type === "undefined" ? "group" : "item";
      return {
        id: item.id,
        title: item.title,
        type: item.type ?? "Group",
        typeKeywords: item.typeKeywords,
        dependencies: solutionData.templates[index].dependencies,
        snippet: item.snippet,
        url: `${portalRoot}/home/${page}.html?id=${item.id}`
      };
    });

    // Otherwise, we can just use the data we have
  } else {
    collection = solutionData.templates.map((template: common.IItemTemplate) => {
      return {
        id: template.itemId,
        title: template.item.title,
        type: template.item.type,
        typeKeywords: template.item.typeKeywords,
        dependencies: template.dependencies,
        snippet: template.item.snippet,
      };
    });
  }
  return collection;
}
